import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from 'components/ui/dialog';
import { Button } from "../../../components/ui/button"
import { Loader2 } from "lucide-react";
import { NewProjectModalProps, Project } from "../types/create-project";
import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { useCreateProject } from '../hooks/use-create-project';
import { CreateProjectRequest } from '../services/projects-services';
import { useTranslation } from 'react-i18next';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useToast } from 'hooks/use-toast';


export const NewProjectModal = ({ isOpen, onClose, newConversatioId, callback }: NewProjectModalProps) => {
    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const setSidebarProjectsList = useChatStore((state) => state.setSidebarProjectsList);
    const [name, setName] = useState("");
    const maxLength = 50;

    const { mutateAsync: createProject, isPending } = useCreateProject();
    const { t } = useTranslation();
    const { toast } = useToast();

    const handleCreateProject = async () => {
        if (!name) return;
        const payload: CreateProjectRequest = {
            name: name,
            conversationId: newConversatioId
        }
        try {
            const response = await createProject({ payload });
            const newProject: Project = {
                id: response.id,
                name: response.name,
                items: [],
                prompts: response.prompts
            }
            setSidebarProjectsList([newProject, ...sidebarProjectsList]);
            callback?.();
        } catch (error) {
            console.log("error creating project", error);
            toast({
                variant: "destructive",
                title: t('ERROR'),
                description: t('FAILED_TO_CREATE_PROJECT'),
            });
        }
        onClose();
        setName("");
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setName(e.target.value);
    };

    useEffect(() => {
        if (isOpen) {
            setName("");
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader className="pb-6">
                    <DialogTitle>
                        {t('PROJECT_NAME')}
                    </DialogTitle>
                </DialogHeader>

                <div className="w-full space-y-2">
                    <Input
                        onClick={(e) => e.stopPropagation()}
                        type='text'
                        placeholder={t('PROJECT_NAME_PLACEHOLDER')}
                        value={name}
                        onChange={handleChange}
                        maxLength={maxLength}
                        onKeyDown={(e) => e.key === "Enter" && !isPending && name.trim() && handleCreateProject()}
                        className="
                                        h-10 w-full rounded-3xl
                                        border border-primary
                                        bg-background pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                    />

                    <div className="flex justify-end">
                        <span className={`text-xs`}>
                            {name.length}/{maxLength}
                        </span>
                    </div>
                </div>

                <DialogFooter className="flex flex-row justify-end space-x-3 pt-6 border-t border-gray-200">
                    <Button
                        variant="outline"
                        className="w-24 h-10"
                        onClick={(e) => { e.stopPropagation(); onClose() }}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        className="h-10"
                        onClick={(e) => { e.stopPropagation(); handleCreateProject() }}
                        disabled={isPending || !name.trim()}
                    >
                        {t('CREATE_PROJECT')}
                        {isPending && (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        )}
                    </Button>
                </DialogFooter>

            </DialogContent>
        </Dialog>
    )
}
